import "../style/testing_pyramid.css";
import { NavLink } from "react-router-dom";
export default function TestingPyramid() {
  const pyramidData = [
    {
      type: "Unit Tests",
      percentage: "70%",
      className: "unit-card",
      characteristics: [
        "Execute in milliseconds",
        "Test single functions/methods",
        "No external dependencies",
        "Easy to write and maintain",
        "Identify localized failures",
      ],
      bestFor: [
        "Business logic",
        "Utility functions",
        "Data transformations",
        "Algorithms",
      ],
    },
    {
      type: "Integration Tests",
      percentage: "20%",
      className: "integration-card",
      characteristics: [
        "Execute in seconds",
        "Test component interactions",
        "May use test databases",
        "More complex setup",
        "Validate integrations",
      ],
      bestFor: [
        "API endpoints",
        "Database operations",
        "Service interactions",
        "Module boundaries",
      ],
    },
    {
      type: "E2E Tests",
      percentage: "10%",
      className: "e2e-card",
      characteristics: [
        "Execute in minutes",
        "Test complete workflows",
        "Full system integration",
        "Expensive to maintain",
        "Mimic user experience",
      ],
      bestFor: [
        "Critical user paths",
        "Purchase flows",
        "Registrations",
        "Core features",
      ],
    },
  ];

  return (
    <div className="pyramid-guide-wrapper">
      {/* ================= HERO BANNER ================= */}
      <section className="hero-guide-banner">
      
          <div className="hero-icon-container">
            <i className="fa-solid fa-puzzle-piece"></i>
          </div>

          <h1>The Testing Pyramid</h1>
          <p>
            A strategic approach to balancing different types of tests to build a
            robust and efficient testing strategy.
          </p>

          <div className="metrics-grid">
            <div className="metric-item">
              <span className="value">Unit</span>
              <span className="label">Fastest</span>
            </div>
            <div className="metric-item">
              <span className="value">E2E</span>
              <span className="label">Most Comprehensive</span>
            </div>
            <div className="metric-item">
              <span className="value">Balance</span>
              <span className="label">Efficiency & Coverage</span>
            </div>
          </div>
  
      </section>

      {/* ================= TAB NAVIGATION ================= */}
               <nav className="nav-tabs">
      <NavLink to="/software-testing" className="nav-tab">
        <i className="fa-solid fa-book-open"></i> Overview
      </NavLink>

      <NavLink to="/testing-types" className="nav-tab">
        <i className="fa-solid fa-box"></i> Types
      </NavLink>

      <NavLink to="/testing-pyramid" className="nav-tab">
        <i className="fa-solid fa-puzzle-piece"></i> Pyramid
      </NavLink>

      <NavLink to="/testing-methods" className="nav-tab">
        <i className="fa-solid fa-code"></i> Methods
      </NavLink>

      <NavLink to="/testing-practices" className="nav-tab">
        <i className="fa-solid fa-lightbulb"></i> Practices
      </NavLink>

      <NavLink to="/testing-tools" className="nav-tab">
        <i className="fa-solid fa-screwdriver-wrench"></i> Tools
      </NavLink>
    </nav>

      {/* ================= MAIN CONTENT ================= */}
      <main className="guide-content-container">
        {/* INTRO */}
        <section className="content-box" id="pyramid-intro">
          <h2 className="section-heading">The Testing Pyramid</h2>
          <p>
            The Testing Pyramid is a framework that helps determine the right
            balance of different test types. It suggests having more unit tests at
            the base, fewer integration tests in the middle, and even fewer
            end-to-end tests at the top.
          </p>
        </section>

        {/* ================= PYRAMID VISUAL ================= */}
        <section className="content-box pyramid-visual-section">
          <div className="testing-pyramid-diagram">
            <div className="pyramid-level top e2e">
              <div className="level-content">
                <h4>E2E Tests</h4>
                <p className="percentage">~10%</p>
                <p className="description">
                  High confidence, slow execution, expensive to maintain
                </p>
              </div>
            </div>

            <div className="pyramid-level middle integration">
              <div className="level-content">
                <h4>Integration Tests</h4>
                <p className="percentage">~20%</p>
                <p className="description">
                  Good confidence, moderate speed, reasonable cost
                </p>
              </div>
            </div>

            <div className="pyramid-level base unit">
              <div className="level-content">
                <h4>Unit Tests</h4>
                <p className="percentage">~70%</p>
                <p className="description">
                  Fast feedback, quick execution, easy to maintain
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ================= DETAIL CARDS ================= */}
        <section className="content-box pyramid-details-section">
          <div className="pyramid-detail-cards">
            {pyramidData.map((level, index) => (
              <div className={`detail-card ${level.className}`} key={index}>
                <h3>{level.type}</h3>
                <p className="percentage-label">
                  {level.percentage} of tests
                </p>

                <h4>Characteristics:</h4>
                <ul>
                  {level.characteristics.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>

                <h4>Best For:</h4>
                <ul>
                  {level.bestFor.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* ================= WHY DISTRIBUTION ================= */}
        <section className="content-box">
          <h2 className="section-heading">Why This Distribution?</h2>

          <div className="two-column-layout">
            <div className="info-column">
              <h3>Speed & Feedback:</h3>
              <p>
                Unit tests provide instant feedback during development. The
                faster you can run tests, the faster you can find bugs earlier.
              </p>
            </div>

            <div className="info-column">
              <h3>Cost Efficiency:</h3>
              <p>
                Unit tests are cheaper to write and maintain. E2E tests are
                expensive, brittle, and time-consuming because they run on real
                paths.
              </p>
            </div>

            <div className="info-column">
              <h3>Maintenance:</h3>
              <p>
                Unit tests break less often and are easier to fix. E2E tests can
                break due to any change in the system, requiring more maintenance
                effort.
              </p>
            </div>

            <div className="info-column">
              <h3>Isolation:</h3>
              <p>
                Unit tests clearly identify what broke. E2E test failures can be
                ambiguous, requiring debugging across multiple system layers.
              </p>
            </div>
          </div>
        </section>

        {/* ================= TESTING TROPHY ================= */}
        <section className="content-box">
          <h2 className="section-heading">
            Alternative: The Testing Trophy
          </h2>

          <p>
            Some teams prefer the "Testing Trophy" model, which emphasizes
            integration tests over unit tests. This approach suggests:
          </p>

          <ul>
            <li>
              <strong>Static Analysis (Base):</strong> Linting, type checking
            </li>
            <li>
              <strong>Unit Tests:</strong> 20-30%. Focus on complex logic.
            </li>
            <li>
              <strong>Integration Tests:</strong> 50-60%. The bulk of tests.
            </li>
            <li>
              <strong>E2E Tests:</strong> 10-20%. Critical user journeys.
            </li>
          </ul>

          <p className="note">
            The trophy model argues that integration tests provide the best
            balance of confidence, speed, and cost. Choose the approach that fits
            your team and project.
          </p>
        </section>
      </main>
    </div>
  );
}
